import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { imageLibraryKeys, listImageLibraryItems, DEFAULT_IMAGE_LIBRARY_PAGE_SIZE } from '../api/imageLibraryApi';

/** Page-owned cache shape; shared list invalidations also refresh loaded batches. */
export function useImageLibraryBatches(externalAccountId: number | null, query: string) {
  const client = useQueryClient();
  const queryKey = useMemo(() => [...imageLibraryKeys.lists(), 'batches', externalAccountId, query], [externalAccountId, query]);
  const recovery = useRef({ key: queryKey, attempted: false });
  if (recovery.current.key !== queryKey) recovery.current = { key: queryKey, attempted: false };
  const result = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) => listImageLibraryItems({ externalAccountId, query, page: pageParam, pageSize: DEFAULT_IMAGE_LIBRARY_PAGE_SIZE, signal }),
    getNextPageParam: (last) => last.images.length && last.page * last.pageSize < last.totalCount ? last.page + 1 : undefined,
    placeholderData: undefined,
    gcTime: 0,
    retry: false,
  });
  const images = useMemo(() => [...new Map(result.data?.pages.flatMap((page) => page.images).map((image) => [image.id, image])).values()], [result.data]);
  const last = result.data?.pages.at(-1);
  const totalCount = last?.totalCount ?? 0;
  const inconsistent = Boolean(last && (images.length > totalCount || (images.length < totalCount && !result.hasNextPage)));
  const reset = useCallback(() => client.resetQueries({ queryKey, exact: true }), [client, queryKey]);
  useEffect(() => {
    if (!inconsistent || result.isFetching || recovery.current.attempted) return;
    recovery.current.attempted = true;
    void reset();
  }, [inconsistent, result.isFetching, reset]);
  return {
    ...result,
    images,
    totalCount,
    inconsistent,
    reset,
    loadMore: () => result.fetchNextPage({ cancelRefetch: false }),
    unavailable: !result.data || result.isPending || result.isRefetching || result.isRefetchError || inconsistent,
  };
}
